// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type LocalDateParts = {
  year: number;
  month: number;
  day: number;
};

export const parseLocalDate = (value: string): LocalDateParts | null => {
  const match = LOCAL_DATE_PATTERN.exec(value);
  if (!match) {
    return null;
  }

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }

  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() + 1 !== month || check.getUTCDate() !== day) {
    return null;
  }

  return { year, month, day };
};

export const isLocalDateString = (value: unknown): value is string => {
  return typeof value === 'string' && parseLocalDate(value) !== null;
};

export const localDateToDate = (value: string): Date | null => {
  const parsed = parseLocalDate(value);
  if (!parsed) {
    return null;
  }

  return new Date(parsed.year, parsed.month - 1, parsed.day);
};

export const formatLocalDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const todayLocalDate = (): string => formatLocalDate(new Date());

export const compareLocalDateStrings = (left?: string | null, right?: string | null): number => {
  const leftParsed = left ? parseLocalDate(left) : null;
  const rightParsed = right ? parseLocalDate(right) : null;
  if (!leftParsed && !rightParsed) {
    return 0;
  }
  if (!leftParsed) {
    return 1;
  }
  if (!rightParsed) {
    return -1;
  }

  const leftKey = leftParsed.year * 10000 + leftParsed.month * 100 + leftParsed.day;
  const rightKey = rightParsed.year * 10000 + rightParsed.month * 100 + rightParsed.day;
  return leftKey - rightKey;
};

